import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useToastNotification } from 'hooks/useToastNotification';
import { useUpdateMapping } from '../../hooks';
import { useWizard } from '../../store';
import { DATE_FORMAT_PRESETS, TIMEZONE_OPTIONS, TIME_FORMAT_PRESETS } from '../../utils';
import {
	Button,
	Card,
	CardDescription,
	CardTitle,
	Checkbox,
	Footer,
	Input,
	Label,
	Row,
	Select,
} from '../../TicketImporter.styles';

export const OptionsStep = (): JSX.Element => {
	const history = useHistory();
	const { pathname } = useLocation();
	const { state, dispatch } = useWizard();
	const { showNotification, TYPES } = useToastNotification();

	const [dateFormat, setDateFormat] = useState(state.dateFormat);
	const [timeFormat, setTimeFormat] = useState(state.timeFormat);
	const [timezone, setTimezone] = useState(state.assumedTimezone);
	const [seatsIOAction, setSeatsIOAction] = useState(state.seatsIOAction);
	const [fixExcelDates, setFixExcelDates] = useState(state.fixExcelDates);
	const [seatLabelTemplate, setSeatLabelTemplate] = useState(state.seatLabelTemplate);
	const [defaultPrice, setDefaultPrice] = useState(state.defaultPrice);
	const [notifyMode, setNotifyMode] = useState(state.notifyMode);

	const { mutate, isLoading } = useUpdateMapping(state.importId as number, {
		onSuccess: () => {
			dispatch({
				type: 'patch',
				payload: {
					dateFormat,
					timeFormat,
					assumedTimezone: timezone,
					seatsIOAction,
					fixExcelDates,
					seatLabelTemplate,
					defaultPrice,
					notifyMode,
				},
			});
			history.push(`${pathname}#6`);
		},
		onError: () => showNotification({ type: TYPES.ERROR, message: 'Could not save options' }),
	});

	const onContinue = () => {
		mutate({
			date_format: dateFormat,
			time_format: timeFormat,
			assumed_timezone: timezone,
			seats_io_action: seatsIOAction,
			fix_excel_dates: fixExcelDates,
			seat_label_template: seatLabelTemplate,
			default_price: defaultPrice,
			notify_mode: notifyMode,
		});
	};

	return (
		<>
			<Card>
				<CardTitle>5. Options</CardTitle>
				<CardDescription>Format and behaviour overrides used when resolving each row.</CardDescription>

				<Row>
					<Label>
						Date format{' '}
						<small style={{ color: '#9ca3af' }}>
							— Go layout (e.g. <code>1/2/06</code> for 5/27/26)
						</small>
						<Select value={dateFormat} onChange={(e) => setDateFormat(e.target.value)}>
							{DATE_FORMAT_PRESETS.map((p) => (
								<option key={p.value} value={p.value}>
									{p.label}
								</option>
							))}
							<option value={dateFormat}>Custom: {dateFormat}</option>
						</Select>
						<Input
							type="text"
							value={dateFormat}
							onChange={(e) => setDateFormat(e.target.value)}
							placeholder="Custom date layout"
						/>
					</Label>
					<Label>
						Time format
						<Select value={timeFormat} onChange={(e) => setTimeFormat(e.target.value)}>
							{TIME_FORMAT_PRESETS.map((p) => (
								<option key={p.value} value={p.value}>
									{p.label}
								</option>
							))}
						</Select>
					</Label>
				</Row>

				<Row>
					<Label>
						Assumed timezone <small style={{ color: '#9ca3af' }}>— how to interpret naive dates from the CSV</small>
						<Select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
							{TIMEZONE_OPTIONS.map((tz) => (
								<option key={tz} value={tz}>
									{tz}
								</option>
							))}
						</Select>
					</Label>
				</Row>
			</Card>

			<Card>
				<CardTitle>Seating</CardTitle>
				<Row>
					<Label>
						Seat label template{' '}
						<small style={{ color: '#9ca3af' }}>
							— use <code>&#123;section&#125;</code>, <code>&#123;row&#125;</code>, <code>&#123;number&#125;</code>.
							Leave empty to use just the seat number.
						</small>
						<Input
							type="text"
							value={seatLabelTemplate}
							onChange={(e) => setSeatLabelTemplate(e.target.value)}
							placeholder="e.g. {row}-{number}"
						/>
					</Label>
				</Row>
				<Row>
					<Label>
						Seats.io action
						<Select value={seatsIOAction} onChange={(e) => setSeatsIOAction(e.target.value as any)}>
							<option value="book">Validate &amp; book (recommended)</option>
							<option value="validate_only">Validate only (do not reserve)</option>
						</Select>
					</Label>
				</Row>
				<Row>
					<Checkbox>
						<input type="checkbox" checked={fixExcelDates} onChange={(e) => setFixExcelDates(e.target.checked)} />
						Auto-fix Excel-mangled seats (e.g. <code>1-Oct → 10-1</code>)
					</Checkbox>
				</Row>
			</Card>

			<Card>
				<CardTitle>Defaults</CardTitle>
				<Row>
					<Label>
						Default price{' '}
						<small style={{ color: '#9ca3af' }}>
							— used when no price column is mapped or the row&apos;s price is blank
						</small>
						<Input
							type="number"
							step="0.01"
							value={defaultPrice}
							onChange={(e) => setDefaultPrice(Number(e.target.value) || 0)}
						/>
					</Label>
				</Row>
			</Card>

			<Card>
				<CardTitle>Notifications</CardTitle>
				<CardDescription>
					Whether to send a confirmation email to each customer right after the import is committed. Uses the same
					template as comp tickets and includes a link to claim the ticket.
				</CardDescription>
				<Row>
					<Label>
						<input
							type="radio"
							name="notify_mode"
							checked={notifyMode === 'none'}
							onChange={() => setNotifyMode('none')}
						/>{' '}
						<strong>Don&apos;t send emails</strong> — tickets are imported silently. The partner can notify customers
						later by another channel.
					</Label>
				</Row>
				<Row>
					<Label>
						<input
							type="radio"
							name="notify_mode"
							checked={notifyMode === 'now'}
							onChange={() => setNotifyMode('now')}
						/>{' '}
						<strong>Send emails now</strong> — one email per buyer, dispatched right after commit.
					</Label>
				</Row>
			</Card>

			<Footer>
				<Button
					variant="secondary"
					onClick={() => history.push(`${pathname}${state.scopeMode === 'fixed' ? '#3' : '#4'}`)}
				>
					← Back
				</Button>
				<Button onClick={onContinue} disabled={isLoading}>
					Continue →
				</Button>
			</Footer>
		</>
	);
};
